import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchSchedulingWindows, type SchedulingWindowSummary } from 'api/scheduling_windows/endpoints/searchSchedulingWindows';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

interface Props {
  // The stored scheduling_window_id, or null for "no restriction".
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name` equals). Undefined widens to
  // every window the user can see.
  readonly zoneName?: string;
}

const getKey = (w: SchedulingWindowSummary) => w.scheduling_window_id;
const getLabel = (w: SchedulingWindowSummary): string => w.name;

/**
 * Searchable dropdown for scheduling windows keyed on `scheduling_window_id` — the id-keyed sibling of
 * {@link EditSchedulingWindowReference} (which keys on `zone_name|name`), for callers that store the numeric id. Props
 * are kept in step with the sibling's; only `value` / `onChange` differ. The caller works in plain ids: `resolve`
 * hydrates a stored id to its name, and selection returns the id.
 *
 * Backed by server-side search, so the option list is not bounded by what fits in one eager fetch.
 */
export const EditSchedulingWindowIdReference = ({ value, onChange, label, placeholder, mandatory, disabled, zoneName }: Props) => {
  // Inflate the stored id to the partial summary EditReference selects on; the empty name defers
  // label resolution to `resolve`. Memoised on the id so the reference model isn't rebuilt each render.
  const selected = useMemo<SchedulingWindowSummary | null>(() => (value !== null ? { scheduling_window_id: value, name: '', zone_name: null } : null), [value]);

  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<SchedulingWindowSummary>>(
    () => ({
      kind: 'search',
      search: (query, limit) => searchSchedulingWindows({ name: query || undefined, limit, zone_name: zoneName }),
      // Resolve a saved id back to its label by id alone — independent of the zone scope, so an
      // existing selection still renders even once the scope no longer admits it.
      resolve: async (key) => {
        const { items } = await searchSchedulingWindows({ scheduling_window_id: Number(key), limit: 1 });
        return items[0] ?? null;
      },
      // Shown while `resolve` is in flight so a key-only value reads as selected rather than blank.
      interimValue: (key) => ({ scheduling_window_id: Number(key), name: `Scheduling window #${key}`, zone_name: null }),
    }),
    [zoneName]
  );

  return (
    <EditReference<SchedulingWindowSummary>
      label={label}
      placeholder={placeholder}
      value={selected}
      onChange={(w) => onChange(w?.scheduling_window_id ?? null)}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(w, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{w.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{w.zone_name}</span>
        </div>
      )}
    />
  );
};
